import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { ORNAMENT_CONFIG } from '../../utils/constants'
import { generateChaosPosition, generateOrnamentPosition, getWeightedProgress } from '../../utils/positions'

interface OrnamentsProps {
  progress: number
}

interface OrnamentData {
  chaosPosition: THREE.Vector3
  targetPosition: THREE.Vector3
  color: THREE.Color
  scale: number
  weight: number
  rotationSpeed: number
}

// 金色发光彩球组件 - 真实金属质感（使用环境贴图反射）
function GoldenBauble({ position, color, scale }: {
  position: THREE.Vector3
  color: THREE.Color
  scale: number
}) {
  return (
    <group position={position} scale={scale}>
      {/* 球体核心 - 真实金属质感 */}
      <mesh>
        <sphereGeometry args={[1, 64, 64]} />
        <meshStandardMaterial
          color={color}
          metalness={1.0}        // 完全金属
          roughness={0.08}       // 非常光滑，镜面反射
          envMapIntensity={2.5}  // 强环境反射
          emissive={color}
          emissiveIntensity={0.1} // 轻微自发光
        />
      </mesh>
      {/* 顶部装饰 - 金色金属 */}
      <mesh position={[0, 1.1, 0]}>
        <cylinderGeometry args={[0.12, 0.15, 0.15, 16]} />
        <meshStandardMaterial
          color="#FFD700"
          metalness={1.0}
          roughness={0.1}
          envMapIntensity={2}
          emissive="#FFD700"
          emissiveIntensity={0.1}
        />
      </mesh>
    </group>
  )
}

// 白色小灯泡组件 - 非常小的白色发光点
function WhiteBulb({ position, scale }: {
  position: THREE.Vector3
  scale: number
}) {
  return (
    <group position={position} scale={scale}>
      {/* 发光核心 - 纯白色 */}
      <mesh>
        <sphereGeometry args={[1, 8, 8]} />
        <meshBasicMaterial color="#FFFFFF" />
      </mesh>
      {/* 中层光晕 */}
      <mesh scale={1.8}>
        <sphereGeometry args={[1, 8, 8]} />
        <meshBasicMaterial color="#FFFFFF" transparent opacity={0.6} />
      </mesh>
      {/* 外层光晕 */}
      <mesh scale={2.5}>
        <sphereGeometry args={[1, 8, 8]} />
        <meshBasicMaterial color="#FFFACD" transparent opacity={0.25} />
      </mesh>
    </group>
  )
}

// 彩色灯光点组件 - 用于其他颜色的灯
function ColoredLight({ position, color, scale }: {
  position: THREE.Vector3
  color: THREE.Color
  scale: number
}) {
  return (
    <group position={position} scale={scale}>
      {/* 发光核心 */}
      <mesh>
        <sphereGeometry args={[1, 8, 8]} />
        <meshBasicMaterial color={color} />
      </mesh>
      {/* 中层光晕 */}
      <mesh scale={2}>
        <sphereGeometry args={[1, 8, 8]} />
        <meshBasicMaterial color={color} transparent opacity={0.5} />
      </mesh>
      {/* 外层光晕 */}
      <mesh scale={3}>
        <sphereGeometry args={[1, 8, 8]} />
        <meshBasicMaterial color={color} transparent opacity={0.2} />
      </mesh>
    </group>
  )
}

export function Ornaments({ progress }: OrnamentsProps) {
  const groupRef = useRef<THREE.Group>(null)
  
  // 生成装饰物数据
  const { baubles, whiteBulbs, coloredLights } = useMemo(() => {
    const baubleData: OrnamentData[] = []
    const whiteBulbData: OrnamentData[] = []
    const coloredLightData: OrnamentData[] = []
    
    // 彩球颜色 - 参考图：80%金色, 20%红色
    const baubleColors = [
      '#FFD700', '#FFD700', '#FFD700', '#FFD700', // 金色
      '#FFC125', '#FFC125', '#FFC125', '#FFC125', // 深金色
      '#FFDF00', '#FFDF00', // 亮金色
      '#c62828', '#b71c1c', // 红色
    ]
    
    // 生成金色彩球 - 增加数量
    const baubleCount = 150 // 金属球数量增多
    for (let i = 0; i < baubleCount; i++) {
      const colorIndex = Math.floor(Math.random() * baubleColors.length)
      baubleData.push({
        chaosPosition: generateChaosPosition(),
        targetPosition: generateOrnamentPosition('bauble'),
        color: new THREE.Color(baubleColors[colorIndex]),
        scale: 0.25 + Math.random() * 0.35, // 尺寸: 0.25-0.6
        weight: ORNAMENT_CONFIG.baubles.weight,
        rotationSpeed: (Math.random() - 0.5) * 3,
      })
    }
    
    // 生成白色小灯泡 - 大幅减少数量
    const whiteBulbCount = 12 // 少量灯泡
    for (let i = 0; i < whiteBulbCount; i++) {
      whiteBulbData.push({
        chaosPosition: generateChaosPosition(),
        targetPosition: generateOrnamentPosition('light'),
        color: new THREE.Color('#FFFFFF'),
        scale: 0.05 + Math.random() * 0.04, // 尺寸: 0.05-0.09
        weight: ORNAMENT_CONFIG.lights.weight,
        rotationSpeed: 0,
      })
    }
    
    // 生成极少量彩色灯光点
    const coloredLightCount = 5
    const lightColors = ['#FFD700', '#FF6B6B', '#FFFFFF']
    for (let i = 0; i < coloredLightCount; i++) {
      const colorIndex = Math.floor(Math.random() * lightColors.length)
      coloredLightData.push({
        chaosPosition: generateChaosPosition(),
        targetPosition: generateOrnamentPosition('light'),
        color: new THREE.Color(lightColors[colorIndex]),
        scale: 0.08 + Math.random() * 0.06,
        weight: ORNAMENT_CONFIG.lights.weight,
        rotationSpeed: 0,
      })
    }
    
    return { baubles: baubleData, whiteBulbs: whiteBulbData, coloredLights: coloredLightData }
  }, [])
  
  // 计算当前位置
  const getCurrentPosition = (data: OrnamentData) => {
    const weightedProgress = getWeightedProgress(progress, data.weight)
    return new THREE.Vector3().lerpVectors(
      data.chaosPosition,
      data.targetPosition,
      weightedProgress
    )
  }
  
  // 动画更新
  const timeRef = useRef(0)
  useFrame((_, delta) => {
    timeRef.current += delta
  })
  
  return (
    <group ref={groupRef}>
      {/* 金色彩球 - 主要装饰 */}
      {baubles.map((bauble, index) => (
        <GoldenBauble
          key={`bauble-${index}`}
          position={getCurrentPosition(bauble)}
          color={bauble.color}
          scale={bauble.scale}
        />
      ))}
      
      {/* 白色小灯泡 - 点缀在金色球之间 */}
      {whiteBulbs.map((bulb, index) => (
        <WhiteBulb
          key={`white-bulb-${index}`}
          position={getCurrentPosition(bulb)}
          scale={bulb.scale * (0.5 + progress * 0.5)}
        />
      ))}
      
      {/* 彩色灯光点 */}
      {coloredLights.map((light, index) => (
        <ColoredLight
          key={`colored-light-${index}`}
          position={getCurrentPosition(light)}
          color={light.color}
          scale={light.scale * progress}
        />
      ))}
    </group>
  )
}